import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import CheckoutForm from './components/CheckoutForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import env from 'env';
import api from 'api';
import ListingContext from '../../providers/Listing/Listing.context';
import { Loader } from 'ncoded-component-library';
import { useNavigate } from 'react-router-dom';
import { transformPriceNumberToCurrencyString } from 'utils';
import {
  BUYER_OUTREACH_PRICES,
  LISTING_PACKAGE_PRICES,
  MEDIA_PACKAGE_PRICES,
} from 'data/prices';
import { TaxInvoice } from './components/TaxInvoice';
import { Field, useFormState } from 'react-final-form';
import { RadioOption } from 'components/RadioGroupField/RadioGroupField.component';
import { PAYMENT_OPTION, PaymentOption } from 'models/Listing';
import RadioSliderField from 'components/RadioSliderField';
import { TermsOfPayment } from './components/TermsOfPayment';
import { BUSINESS_ROUTE } from 'data/global';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './Checkout.styles.scss';
import './Checkout.styles.responsive.scss';

export const TAX_INVOICE = 'taxInvoice';

type CheckoutProps = {
  className?: string;
};

const Checkout: React.FC<CheckoutProps> = (props) => {
  const { className } = props;

  const [clientSecret, setClientSecret] = useState<string>();

  const {
    listing: { listingPackage, mediaPackage, buyerOutreach, _id: listingId },
  } = useContext(ListingContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    values: { paymentOption },
  } = useFormState();

  const listingPackagePrice = useMemo(
    () => LISTING_PACKAGE_PRICES[listingPackage],
    [listingPackage],
  );

  const mediaPackagePrice = useMemo(
    () => MEDIA_PACKAGE_PRICES[mediaPackage],
    [mediaPackage],
  );

  const buyerOutreachPrice = useMemo(
    () => BUYER_OUTREACH_PRICES[buyerOutreach],
    [buyerOutreach],
  );

  const subtotal = useMemo(
    () =>
      (listingPackagePrice || 0) +
      (mediaPackagePrice || 0) +
      (buyerOutreachPrice || 0),
    [buyerOutreachPrice, listingPackagePrice, mediaPackagePrice],
  );

  const gst = useMemo(() => subtotal / 10, [subtotal]);

  const total = useMemo(() => subtotal + gst, [gst, subtotal]);

  const isInvoicePayment = useMemo(
    () => paymentOption === PAYMENT_OPTION.INVOICE,
    [paymentOption],
  );

  const paymentOptions = useMemo<RadioOption<PaymentOption>[]>(
    () => [
      { label: t('creditCard'), value: PAYMENT_OPTION.CREDIT_CARD },
      { label: t('invoice'), value: PAYMENT_OPTION.INVOICE },
    ],
    [t],
  );

  const checkoutItems = useMemo(
    () => [
      {
        label: 'listingPackage',
        value: listingPackagePrice,
      },
      {
        label: 'mediaPackage',
        value: mediaPackagePrice,
      },
      {
        label: 'buyerOutreach',
        value: buyerOutreachPrice,
      },
      {
        label: 'subtotal',
        value: subtotal,
      },
      {
        label: 'gst',
        value: gst,
      },
      {
        label: 'total',
        value: total,
      },
    ],
    [
      buyerOutreachPrice,
      gst,
      listingPackagePrice,
      mediaPackagePrice,
      subtotal,
      total,
    ],
  );

  const classes = classNames('checkout', 'lcw-page', className);

  useEffect(() => {
    listingId &&
      (async () => {
        try {
          const {
            data: { clientSecret },
          } = await api.listings.createPaymentIntent(listingId);
          setClientSecret(clientSecret);
        } catch (e) {
          navigate(BUSINESS_ROUTE, { replace: true });
        }
      })();
  }, [listingId, navigate]);

  return (
    <div className={classes}>
      <div className="lcw-page__heading">
        <p className="lcw-page__heading__title">{t('payment')}</p>
      </div>

      <div className="checkout__content">
        <div className="checkout__content__left">
          <div className="checkout__content__left__pricing white-panel">
            {checkoutItems.map(({ label, value }) => {
              if (!value) return null;

              return (
                <div key={label} className="pricing__row">
                  <p>{t(label)}</p>
                  <p>{transformPriceNumberToCurrencyString(value)}</p>
                </div>
              );
            })}
          </div>

          <TaxInvoice />
        </div>

        <div className="checkout__content__payment-form-container white-panel">
          {!isSuperAdmin && (
            <Field
              name="paymentOption"
              slideSteps={paymentOptions}
              component={RadioSliderField}
            />
          )}

          {isInvoicePayment ? (
            <TermsOfPayment amount={total} />
          ) : clientSecret ? (
            <Elements
              stripe={loadStripe(env.STRIPE_KEY)}
              options={{ clientSecret }}
            >
              <CheckoutForm />
            </Elements>
          ) : (
            <Loader />
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
